/**
 * Zentrale Inhalts-Konstanten für die Startseite. Später (Phase 2/3) werden
 * diese in MongoDB verlagert und via Admin editierbar. Deshalb jetzt schon als
 * saubere Objekte strukturiert.
 */

/** Verfügbare Hero-Hintergründe der Startseite. Auswahl per CMS-Key
 *  `home.hero.background` — Werte siehe HERO_BG_MAP.
 *  Bewusst nur deutsche Motive (Bergisches / Norddeutsch / Modern) —
 *  südländisch-toskanische Anmutungen wurden verworfen.
 *  `overlayEnd` = Alpha des Dark-Ink-Gradients am unteren Rand (0–1).
 *  Höher = mehr Kontrast für weißen Text bei hellen Bildern. */
export type HeroBgKey =
    | "bergisches-landhaus"
    | "klinker-familienhaus"
    | "landhaus-modern"
    | "villa-original";
export const HERO_BG_MAP: Record<HeroBgKey, {
    label: string; hint: string; src: string; overlay: number;
    overlayEnd?: number;
}> = {
    "bergisches-landhaus": {
        label: "Bergisches Landhaus",
        hint: "Empfohlen · Rheinland",
        src: "/media/homepage/hero-de-bergisches-landhaus.jpg",
        overlay: 0.38,
        // Bild ist mittel-hell (Fassade cremig) → kräftigeres Bottom-Overlay
        overlayEnd: 0.92,
    },
    "klinker-familienhaus": {
        label: "Klinker-Familienhaus",
        hint: "Niederrhein · Herbstlicht",
        src: "/media/homepage/hero-de-klinker-familienhaus.jpg",
        overlay: 0.36,
        // Klinker ist dunkler → etwas weniger Overlay
        overlayEnd: 0.88,
    },
    "landhaus-modern": {
        label: "Modernes Landhaus",
        hint: "Blaue Stunde",
        src: "/media/homepage/hero-de-landhaus-modern.jpg",
        overlay: 0.42,
        // Blaue Stunde ist bereits dunkel → schwächeres Overlay
        overlayEnd: 0.82,
    },
    "villa-original": {
        label: "Originalbild (Villa)",
        hint: "Ursprüngliches Foto · Legacy-Hero",
        src: "/media/homepage/hero-villa.jpg",
        overlay: 0.28,
    },
};
export const HERO_BG_DEFAULT: HeroBgKey = "bergisches-landhaus";

export const HERO = {
    eyebrow: "HypeFly · Premium-Immobiliendokumentation",
    headline: "Heute festgehalten.\nFür Generationen bewahrt.",
    subline:
        "Wir dokumentieren Haus, Grundstück und Umgebung als vollständiges 360°-Video-Zeitdokument — ergänzt um hochwertige 360°-Fotos.",
    usps: ["360°-Video", "frei erkundbar", "dauerhaft bewahrt"],
    image: HERO_BG_MAP[HERO_BG_DEFAULT].src,
};

export const IDEA = {
    eyebrow: "Die Idee",
    headline: "Ein digitales Gedächtnis Ihrer Immobilie.",
    paragraphs: [
        "Nicht nur ein Foto. Nicht nur ein Film. Ein vollständiger Ort, den Sie in einigen Jahren noch einmal genauso erleben können, wie er heute war.",
        "Wir kommen zu Ihnen, dokumentieren jede Ecke Ihres Anwesens mit einer echten 360°-Video-Perspektive — ergänzt um hochwertige 360°-Fotos für ruhige Details — und übergeben Ihnen ein Archiv, das bleibt, auch wenn Möbel wandern, Bäume gefällt werden oder Räume umgestaltet.",
        "So wird aus einem Haus eine Geschichte, die sich erzählen lässt. Für Ihre Familie, für kommende Generationen, für den Verkauf. Ganz nach Ihrem Wunsch.",
    ],
    image: "/media/homepage/garden-detail.jpg",
};

export const IMMERSIVE = {
    eyebrow: "360°-Video erleben",
    headline: "Sehen Sie sich selbst um.",
    lead:
        "Ein Ausschnitt aus unserem echten 360°-Video-Archiv. Mit Maus oder Finger bewegen. Während des Videos den Blick frei in alle Richtungen verändern. Zoomen mit Mausrad oder Pinch. Vollbild für Präsentation.",
};

export const LENSES = {
    eyebrow: "Die Technik",
    headline: "Zwei Linsen. Ein vollständiger Raum.",
    paragraphs: [
        "Zwei ungefähr 180°-Bildhälften erfassen alles gleichzeitig — vorne, hinten, oben, unten. Wir setzen sie zu einer echten Rundum-Kugel zusammen.",
        "Nichts wird ausgeschnitten, nichts wird vorentschieden. Was Sie später sehen möchten, entscheiden Sie beim Betrachten selbst.",
    ],
};

export const COMPARE = [
    {
        key: "photo",
        title: "Klassisches Foto",
        icon: "camera",
        bullets: [
            "Ein einziger, fester Blickwinkel",
            "Alles daneben, dahinter und darüber fehlt",
            "Rahmen entscheidet der Fotograf, nicht Sie",
        ],
    },
    {
        key: "video",
        title: "Klassisches Video",
        icon: "film",
        bullets: [
            "Kamera-Schwenk gibt den Weg vor",
            "Zurückschauen unmöglich",
            "Kein späteres Erkunden möglich",
        ],
    },
    {
        key: "hypefly",
        title: "HypeFly 360°",
        icon: "sphere",
        highlight: true,
        bullets: [
            "Volle Kugel — jede Blickrichtung, immer",
            "Später frei erkundbar, auch nach Jahren",
            "Video und Foto in einem Format vereint",
        ],
    },
];

// USE_CASES als Bild-Kacheln (deutscher Kontext, editorial). Icon-Feld bleibt für
// Reversibilität erhalten, wird aber im aktuellen Layout nicht mehr gerendert.
// old (pre 2025-icon-tiles): reine Text-Kacheln mit Lucide-Icons.
export const USE_CASES = [
    { title: "Erinnerung für Familien", body: "Das Zuhause festhalten, wie es heute lebt.", icon: "heart",
        image: "/media/homepage/wann-familien-erinnerung.jpg",
        alt: "Deutsches Landhaus in Eiche, Kinderfahrrad am Eingang, warmes Fensterlicht" },
    { title: "Vor Umbau", body: "Der Ursprungszustand bleibt vollständig erhalten.", icon: "hammer",
        image: "/media/homepage/wann-vor-umbau.jpg",
        alt: "Würdiges deutsches Einfamilienhaus mit Bauplan-Details vor Umbau" },
    { title: "Vor Sanierung", body: "Alle Details dokumentiert für Planung und Behörden.", icon: "ruler",
        image: "/media/homepage/wann-vor-sanierung.jpg",
        alt: "Würdevolle deutsche Villa mit sichtbarem Sanierungsbedarf im warmen Licht" },
    { title: "Vor Verkauf", body: "Interessenten den Ort echt erlebbar machen.", icon: "key",
        image: "/media/homepage/wann-vor-verkauf.jpg",
        alt: "Gepflegtes deutsches Einfamilienhaus mit Buchsbaum-Hecke vor Verkauf" },
    { title: "Familienhaus für Generationen", body: "Ein Zeitdokument für Kinder und Enkel.", icon: "tree",
        image: "/media/homepage/wann-generationen.jpg",
        alt: "Klassisches deutsches Familienhaus mit Kastanie und Steildach" },
    { title: "Ferienhaus / Anwesen", body: "Besondere Immobilien seriös in Szene.", icon: "sun",
        image: "/media/homepage/wann-ferienhaus.jpg",
        alt: "Bergisches Ferienhaus am See mit Zedernholz und Naturstein" },
    { title: "Historische Immobilien", body: "Substanz und Details digital bewahren.", icon: "book",
        image: "/media/homepage/wann-historisch.jpg",
        alt: "Deutsches Fachwerkhaus mit Kopfsteinpflaster-Hof" },
    { title: "Garten & Grundstück", body: "Bepflanzung, Wege, Ausblick — alles bleibt.", icon: "leaf",
        image: "/media/homepage/wann-garten-grundstueck.jpg",
        alt: "Deutscher Landschaftsgarten mit Buchsbaumparterres und Zierteich" },
    { title: "Vorher / Nachher", body: "Zwei 360°-Dokumentationen im Vergleich.", icon: "arrows",
        image: "/media/sylt/ba-fassade-vorher.jpg",
        alt: "Fassade eines norddeutschen Reetdach-Hauses als Vorher-Aufnahme" },
    { title: "Baufortschritt", body: "Meilensteine Schritt für Schritt festhalten.", icon: "hardhat",
        image: "/media/homepage/wann-baufortschritt.jpg",
        alt: "Deutscher Neubau in der Rohbauphase mit Dachstuhl und Klinker" },
    { title: "Nachlass & Geschichte", body: "Familiengeschichten mit Ort verankern.", icon: "archive",
        image: "/media/homepage/wann-nachlass.jpg",
        alt: "Deutsches Herrenhaus im Herbstlicht mit Kastanienlaub" },
];

// Mediengalerie: nur deutscher Bildkontext. FISHEYE / TINY PLANET wurden
// entfernt (kein reales Asset) — Definitionen als Kommentar stehen gelassen,
// falls echte Assets nachgereicht werden.
export const GALLERY = [
    // 360°-Aufnahmen (echte Assets aus POC)
    // Für photo360: `sphere` (equirektangulare 2:1 Quelle für den Photo-Sphere-Viewer)
    // und `thumb` (Kachel-Vorschau) sind bewusst getrennt.
    { id: "g1", type: "photo360", label: "360° FOTO", src: "/media/demo-360-photo.jpg", sphere: "/media/demo-360-photo.jpg", thumb: "/media/demo-360-poster.jpg", title: "Landhaus · Vorplatz", alt: "360°-Aufnahme eines deutschen Landhaus-Vorplatzes" },
    { id: "g2", type: "video360", label: "360° VIDEO", src: "/media/demo-360-web.mp4", poster: "/media/demo-360-poster.jpg", thumb: "/media/demo-360-poster.jpg", title: "Landhaus · Rundgang", alt: "360°-Video-Rundgang durch ein deutsches Landhaus" },
    // klassische Foto-Kacheln (deutsche Motive)
    { id: "g3", type: "photo", label: "FOTO", src: "/media/homepage/wann-generationen.jpg", thumb: "/media/homepage/wann-generationen.jpg", title: "Familienhaus · Sommerlicht", alt: "Klassisches deutsches Familienhaus mit Kastanie im Sommerlicht" },
    { id: "g4", type: "photo", label: "VIDEO", src: "/media/homepage/wann-garten-grundstueck.jpg", thumb: "/media/homepage/wann-garten-grundstueck.jpg", title: "Garten · Rundgang", alt: "Deutscher Landschaftsgarten mit Buchsbaumparterres", playOverlay: true },
    // old (pre-2025-german-context): TINY PLANET / FISHEYE — echte Assets fehlen aktuell
    // { id: "g5", type: "photo", label: "TINY PLANET", src: "/media/homepage/gallery-03.jpg", thumb: "/media/homepage/gallery-03.jpg", title: "Grundstück · Draufsicht" },
    // { id: "g6", type: "photo", label: "FISHEYE", src: "/media/homepage/gallery-04.jpg", thumb: "/media/homepage/gallery-04.jpg", title: "Eingangsbereich" },
    // Vorher / Nachher aus dem Sylt-Set als visueller Anker
    { id: "g7", type: "photo", label: "VORHER", src: "/media/sylt/ba-fassade-vorher.jpg", thumb: "/media/sylt/ba-fassade-vorher.jpg", title: "Fassade · Ausgangszustand", alt: "Fassade eines norddeutschen Hauses vor der Restaurierung" },
    { id: "g8", type: "photo", label: "NACHHER", src: "/media/sylt/ba-fassade-nachher.jpg", thumb: "/media/sylt/ba-fassade-nachher.jpg", title: "Fassade · nach Restaurierung", alt: "Fassade nach fachgerechter Restaurierung" },
];

export const PROCESS = [
    { step: "01", title: "Vorgespräch", body: "Wir klären Ihre Ziele, Räume, Umfang und den passenden Termin — telefonisch oder persönlich." },
    { step: "02", title: "Planung", body: "Wir stimmen Aufnahmereihenfolge, Kamerapositionen und Details mit Ihnen ab." },
    { step: "03", title: "360°-Video-Aufnahme vor Ort", body: "Ruhig, respektvoll, gründlich. Ein Termin, zwei Linsen, vollständige Video- und Foto-Dokumentation." },
    { step: "04", title: "Aufbereitung & Übergabe", body: "Wir liefern die 360°-Videos und -Fotos digital, browserfähig und dauerhaft archivierbar." },
];

export const PACKAGES = [
    {
        key: "essential",
        name: "HypeFly Essential",
        recommended: false,
        bullets: [
            "Ein Aufnahmetermin bis 2 Stunden vor Ort",
            "Wesentliche 360°-Video-Perspektiven innen und außen",
            "Digitale Übergabe · direkt online betrachtbar",
            "Datei-Format für die eigene Archivierung",
        ],
        price: "Preis auf Anfrage",
    },
    {
        key: "signature",
        name: "HypeFly Signature",
        recommended: true,
        bullets: [
            "Ausführlicher Aufnahmetermin bis 4 Stunden",
            "Vollständige 360°-Video-Dokumentation inkl. Garten & Grundstück",
            "Kuratierte 360°-Video-Sequenz + 360°-Foto-Serie",
            "Private Betrachtungsseite mit teilbarem Link",
            "Zusatz: klassische Foto-Highlights",
        ],
        price: "Preis auf Anfrage",
    },
    {
        key: "legacy",
        name: "HypeFly Legacy",
        recommended: false,
        bullets: [
            "Zwei getrennte Aufnahmetermine (z.B. Sommer/Winter oder vor/nach Umbau)",
            "Umfassende 360°-Video-Dokumentation innen und außen",
            "Kuratierte Erzählung: 360°-Video-Rundgang + Detail-Videos + Detail-Fotos",
            "Digitale Übergabe + dauerhafte, sichere Archivierung",
            "Persönliche Übergabe vor Ort",
        ],
        price: "Preis auf Anfrage",
    },
];

export const ABOUT = {
    eyebrow: "Über HypeFly",
    headline: "Handschrift. Diskretion. Sorgfalt.",
    paragraphs: [
        "HypeFly ist eine Marke der BDV GmbH. Wir kommen aus Düsseldorf, arbeiten bundesweit und dokumentieren Immobilien mit demselben Anspruch, mit dem wir seit über 15 Jahren mit Menschen und Unternehmen arbeiten: persönlich, verlässlich, ohne Show.",
        "Jede Immobilie hat ihre eigene Geschichte. Wir planen jedes Projekt individuell, gehen respektvoll mit dem Ort um und behandeln private Aufnahmen so, wie es sich für ein Familienarchiv gehört.",
    ],
    image: "/media/homepage/about-editorial.jpg",
};

export const FAQ = [
    { q: "Was genau ist eine 360°-Video-Dokumentation?", a: "Statt einzelner Fotos oder eines linearen Videos halten wir jeden Aufnahmepunkt als vollständige 360°-Video-Kugel fest — ergänzt um hochwertige 360°-Fotos für ruhige Details. Sie können später frei entscheiden, wohin Sie blicken." },
    { q: "Wie lange dauert eine Aufnahme vor Ort?", a: "Je nach Objekt zwischen 2 und 4 Stunden. Für sehr umfangreiche Anwesen oder Sonderprojekte planen wir mehrere Termine." },
    { q: "Bekomme ich die Daten dauerhaft?", a: "Ja. Sie erhalten die 360°-Videos und -Fotos in einem archivfähigen Format zur eigenen Aufbewahrung." },
    { q: "Kann ich das Ergebnis auf meinem Handy ansehen?", a: "Ja. Ihre 360°-Video-Dokumentation läuft im Browser auf jedem modernen Smartphone, Tablet und PC — ohne App-Installation." },
    { q: "Werden die Bilder öffentlich?", a: "Nein. Alle Aufnahmen bleiben privat, es sei denn, Sie wünschen ausdrücklich eine öffentliche oder halb-öffentliche Präsentation." },
    { q: "Können auch nur einzelne Räume dokumentiert werden?", a: "Ja. Wir stimmen den Umfang vorher genau mit Ihnen ab." },
    { q: "Was, wenn wir Innen und Außen wollen?", a: "Alle Pakete decken Innen und Außen ab; im Legacy-Paket dokumentieren wir zusätzlich zwei zeitlich getrennte Zustände." },
    { q: "Ist das für den Verkauf einer Immobilie geeignet?", a: "Ja. Ein 360°-Video-Rundgang ist ein starkes Argument für ernsthafte Interessenten." },
    { q: "Wie sieht es mit Denkmalschutz-Objekten aus?", a: "Kein Problem — wir arbeiten mit besonderer Sorgfalt und stimmen sensible Punkte mit Ihnen ab." },
    { q: "Können wir das Material selbst weiter bearbeiten?", a: "Ja. Sie erhalten alle Rohdaten sowie die aufbereitete Fassung." },
    { q: "Kostet das eine Menge?", a: "Der Preis richtet sich nach Umfang, Anfahrtsweg und Aufwand. Wir nennen ihn transparent im Erstgespräch — ohne versteckte Nebenkosten." },
    { q: "Wie schnell bekomme ich das Ergebnis?", a: "In der Regel innerhalb von 10 bis 15 Werktagen nach der Aufnahme." },
    { q: "Was passiert bei schlechtem Wetter?", a: "Wir verschieben Außenaufnahmen. Innenaufnahmen sind wetterunabhängig." },
    { q: "Kann ich später weitere Aufnahmen ergänzen?", a: "Ja — dafür ist besonders das Legacy-Paket gedacht." },
    { q: "Wie steht HypeFly zu meiner Immobilie in fünf Jahren?", a: "Wir sind Teil der BDV-Gruppe seit 2004. Kontinuität ist Teil des Versprechens." },
];

export const OBJECT_TYPES = [
    "Einfamilienhaus",
    "Villa",
    "Landhaus",
    "Wohnung",
    "Gewerblich",
    "Sonstiges",
];

export const PACKAGE_OPTIONS = [
    "HypeFly Essential",
    "HypeFly Signature",
    "HypeFly Legacy",
    "Sonstiges / Beratung",
];

export const BDV_CONTACT = {
    company: "BDV Büroservice Dienstleistungs-Vermittlungs GmbH",
    brand: "HypeFly · eine Marke der BDV GmbH",
    street: "Grafenberger Allee 293",
    zip: "40237",
    city: "Düsseldorf",
    phone: "0211 61 66 33 00",
    phoneHref: "+4921161663300",
    email: "info@hypefly.de",
    fax: "0211 61 66 33 20",
    hrb: "HRB 49554 · AG Düsseldorf",
    ceo: "Gerhard Kirchner",
    vat: "DE234734289",
};
